// Reusable UI building-blocks for Demo sub-pages.
import React from "react";
import { Box, Typography } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import { demo, glassCard } from "./demoStyles";

// ── Metric card ──────────────────────────────────────────────────────────────
export const MetricCard = ({ icon: Icon, label, value, unit, sub, trend, trendUp, gradient, accent }) => {
  const accentColor = accent || demo.accent;
  return (
    <Box
      sx={{
        ...glassCard,
        p: 2.6,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.22s ease, border-color 0.22s ease",
        "&:hover": { transform: "translateY(-3px)", borderColor: demo.borderStrong },
        // Faint gradient tint top-right
        "&::before": {
          content: '""',
          position: "absolute",
          top: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: gradient
            ? `radial-gradient(circle, ${gradient.replace("linear-gradient(135deg,", "").split(",")[0]} 0%, transparent 65%)`
            : `radial-gradient(circle, rgba(34,211,238,0.12) 0%, transparent 65%)`,
          pointerEvents: "none",
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: gradient || `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`,
          boxShadow: "0 6px 18px rgba(0,0,0,0.30)",
          mb: 1,
        }}
      >
        {Icon && <Icon sx={{ color: "#fff", fontSize: 22 }} />}
      </Box>

      {/* Value */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
        <Typography sx={{ color: demo.text, fontWeight: 800, fontSize: 26, lineHeight: 1 }}>
          {value}
        </Typography>
        {unit && (
          <Typography sx={{ color: demo.textDim, fontSize: 13, fontWeight: 600 }}>{unit}</Typography>
        )}
      </Box>

      {/* Label */}
      <Typography sx={{ color: demo.textDim, fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
        {label}
      </Typography>

      {/* Trend / sub-text */}
      {(trend || sub) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.6 }}>
          {trend && (
            <>
              {trendUp
                ? <TrendingUp sx={{ fontSize: 14, color: demo.green }} />
                : <TrendingDown sx={{ fontSize: 14, color: "#f87171" }} />}
              <Typography sx={{ fontSize: 12, color: trendUp ? demo.green : "#f87171", fontWeight: 600 }}>
                {trend}
              </Typography>
            </>
          )}
          {sub && !trend && (
            <Typography sx={{ fontSize: 12, color: demo.textFaint }}>{sub}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// ── Section heading ──────────────────────────────────────────────────────────
export const SectionTitle = ({ children, right }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
    <Typography sx={{ color: demo.text, fontWeight: 700, fontSize: 16 }}>{children}</Typography>
    {right && <Box>{right}</Box>}
  </Box>
);

// ── Chart wrapper card ───────────────────────────────────────────────────────
export const ChartCard = ({ title, subtitle, children, sx }) => (
  <Box sx={{ ...glassCard, p: "22px 24px 20px", ...sx }}>
    {title && (
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ color: demo.text, fontWeight: 700, fontSize: 15 }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ color: demo.textDim, fontSize: 12.5, mt: 0.3 }}>{subtitle}</Typography>
        )}
      </Box>
    )}
    {children}
  </Box>
);

// ── Custom recharts tooltip ───────────────────────────────────────────────────
export const DarkTooltip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: "rgba(12,17,28,0.96)",
        border: `1px solid ${demo.border}`,
        borderRadius: "10px",
        px: 1.8,
        py: 1.2,
        backdropFilter: "blur(10px)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
        minWidth: 120,
      }}
    >
      {label !== undefined && (
        <Typography sx={{ color: demo.textDim, fontSize: 11.5, mb: 0.8, fontWeight: 600 }}>
          {label}
        </Typography>
      )}
      {payload.map((p, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: i < payload.length - 1 ? 0.5 : 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
          <Typography sx={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>
            {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
            {unit && <Box component="span" sx={{ color: demo.textDim, fontWeight: 500, ml: 0.4 }}>{unit}</Box>}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── Status dot ───────────────────────────────────────────────────────────────
export const StatusDot = ({ color }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
);

// ── Alert row ────────────────────────────────────────────────────────────────
export const AlertRow = ({ severity, system, message, time }) => {
  const colors = { critical: "#f87171", warning: demo.amber, info: demo.accent, resolved: demo.green };
  const color  = colors[severity] || demo.textDim;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.4,
        py: 1.4,
        borderBottom: `1px solid ${demo.border}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ mt: 0.4, width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0, boxShadow: `0 0 7px ${color}` }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
          <Typography sx={{ color: demo.text, fontSize: 13.5, fontWeight: 600, lineHeight: 1.2 }}>
            {message}
          </Typography>
        </Box>
        <Typography sx={{ color: demo.textFaint, fontSize: 11.5 }}>
          {system} · {time}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 1.1, py: 0.35, borderRadius: "5px",
          background: `${color}18`,
          border: `1px solid ${color}35`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color, fontSize: 10.5, fontWeight: 700, textTransform: "capitalize" }}>
          {severity}
        </Typography>
      </Box>
    </Box>
  );
};
